import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { apiFetch, ApiError } from "../../lib/api";
import { photoUrl } from "../../lib/assets";
import { ADMIN_DASHBOARD_PATH, adminHomeHeroPhotoEditPath } from "../../lib/adminPaths";

export default function AdminHomeHeroPhotosPage() {
  const { logout } = useAdminAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [reorderingId, setReorderingId] = useState(null);
  const [error, setError] = useState("");

  const loadPhotos = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const body = await apiFetch("/admin/home-hero-photos");
      setPhotos(body.data.photos || []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't load photos — try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    void loadPhotos();
  }, [loadPhotos]);

  function handleFileChange(event) {
    setFiles(Array.from(event.target.files || []));
    setError("");
  }

  async function handleUpload(event) {
    event.preventDefault();
    if (files.length === 0) {
      setError("Choose one or more images first.");
      return;
    }

    setUploading(true);
    setError("");

    try {
      const formData = new FormData();
      for (const file of files) {
        formData.append("photos", file);
      }

      await apiFetch("/admin/home-hero-photos/batch", {
        method: "POST",
        body: formData,
      });

      setFiles([]);
      event.target.reset();
      await loadPhotos();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Upload failed — try again.");
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this photo? This can't be undone.")) {
      return;
    }

    setDeletingId(id);
    setError("");

    try {
      await apiFetch(`/admin/home-hero-photos/${id}`, { method: "DELETE" });
      await loadPhotos();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Delete failed — try again.");
    } finally {
      setDeletingId(null);
    }
  }

  async function handleMove(index, direction) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= photos.length) {
      return;
    }

    const nextPhotos = [...photos];
    [nextPhotos[index], nextPhotos[targetIndex]] = [nextPhotos[targetIndex], nextPhotos[index]];

    const movedId = photos[index].id;
    const previousPhotos = photos;

    setReorderingId(movedId);
    setPhotos(nextPhotos);
    setError("");

    try {
      const body = await apiFetch("/admin/home-hero-photos/reorder", {
        method: "PATCH",
        body: JSON.stringify({
          order: nextPhotos.map((photo) => photo.id),
        }),
      });
      setPhotos(body.data.photos || nextPhotos);
    } catch (err) {
      setPhotos(previousPhotos);
      setError(err instanceof ApiError ? err.message : "Couldn't reorder photos — try again.");
    } finally {
      setReorderingId(null);
    }
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div>
          <Link className="admin-back-link" to={ADMIN_DASHBOARD_PATH}>
            ← Dashboard
          </Link>
          <p className="admin-eyebrow">HOME PAGE</p>
          <h1 className="admin-title admin-title--compact">Home Page Photo Show</h1>
        </div>
        <button className="admin-button admin-button--ghost" type="button" onClick={() => void logout()}>
          Sign out
        </button>
      </header>

      <section className="admin-card admin-card--compact-upload">
        <form className="admin-upload-row" onSubmit={(event) => void handleUpload(event)}>
          <div className="admin-upload-row__field">
            <label className="admin-label" htmlFor="hero-photos">
              Upload images
            </label>
            <input
              className="admin-input"
              id="hero-photos"
              name="photos"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              onChange={handleFileChange}
            />
          </div>
          <button
            className="admin-button admin-upload-row__button"
            type="submit"
            disabled={uploading || files.length === 0}
          >
            {uploading
              ? "Uploading…"
              : files.length > 1
                ? `Upload ${files.length} photos`
                : "Upload"}
          </button>
        </form>
        {error ? <p className="admin-error">{error}</p> : null}
      </section>

      <section className="admin-card" style={{ marginTop: 20 }}>
        <div className="admin-section-header">
          <div>
            <p className="admin-eyebrow">Carousel</p>
            <h2 className="admin-tool-card__title">Photos</h2>
          </div>
          <span className="admin-muted">{photos.length} total</span>
        </div>

        {loading ? <p className="admin-muted">Loading photos…</p> : null}

        {!loading && photos.length === 0 ? (
          <p className="admin-muted">No photos yet — upload images above, then edit each one to add metadata.</p>
        ) : null}

        {!loading && photos.length > 1 ? (
          <p className="admin-muted admin-muted--spaced">
            Use the arrows to change order on the home page carousel and photography showcase. The first photo is shown initially on the home page.
          </p>
        ) : null}

        <ul className="admin-photo-list">
          {photos.map((photo, index) => (
            <li key={photo.id} className="admin-photo-item">
              <div className="admin-photo-item__order">
                <span className="admin-photo-item__position">{index + 1}</span>
                <div className="admin-photo-item__order-buttons">
                  <button
                    className="admin-order-button"
                    type="button"
                    aria-label={`Move photo ${index + 1} up`}
                    disabled={index === 0 || reorderingId !== null || deletingId !== null}
                    onClick={() => void handleMove(index, -1)}
                  >
                    ↑
                  </button>
                  <button
                    className="admin-order-button"
                    type="button"
                    aria-label={`Move photo ${index + 1} down`}
                    disabled={
                      index === photos.length - 1 || reorderingId !== null || deletingId !== null
                    }
                    onClick={() => void handleMove(index, 1)}
                  >
                    ↓
                  </button>
                </div>
              </div>
              <img
                className="admin-photo-item__thumb"
                src={photoUrl(photo)}
                alt={photo.title || "Carousel photo"}
              />
              <div className="admin-photo-item__body">
                <h3 className="admin-photo-item__title">{photo.title || "Untitled"}</h3>
                {photo.description ? (
                  <p className="admin-photo-item__description">{photo.description}</p>
                ) : (
                  <p className="admin-muted">No description yet.</p>
                )}
                {photo.keywords?.length ? (
                  <p className="admin-photo-item__keywords">
                    {photo.keywords.map((keyword) => (
                      <span key={keyword} className="admin-tag">
                        {keyword}
                      </span>
                    ))}
                  </p>
                ) : (
                  <p className="admin-muted">No keywords yet.</p>
                )}
              </div>
              <div className="admin-photo-item__actions">
                <Link className="admin-button admin-button--ghost" to={adminHomeHeroPhotoEditPath(photo.id)}>
                  Edit metadata
                </Link>
                <button
                  className="admin-button admin-button--ghost admin-button--danger"
                  type="button"
                  disabled={deletingId === photo.id || reorderingId !== null}
                  onClick={() => void handleDelete(photo.id)}
                >
                  {deletingId === photo.id ? "Deleting…" : "Delete"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
